import * as storage from "@/lib/storage";
import { AUTH_RETURN_PATH_KEY, REDIRECT_PATH_KEY, REQUIRE_ADDITIONAL_INFO_KEY } from "./constants";

const NON_RETURN_PATHS = ["/login", "/", "/404", "/unauthorized", "/additional-info"];

// Query parameter used only on /additional-info to survive UI work before final navigation.
const ADDITIONAL_INFO_RETURN_PARAM = "returnTo";

// Prefix marks TPass state values that belong to our return-path fallback.
const AUTH_RETURN_STATE_PREFIX = "echo_return:";

// Short-lived first-party fallback for cross-site auth redirects in private browsing.
const AUTH_RETURN_PATH_COOKIE = "echo_auth_return_path";

// Return paths only need to survive the external login round trip.
const RETURN_PATH_COOKIE_MAX_AGE_SECONDS = 30 * 60;

export const getCurrentReturnPath = () => {
  if (typeof window === "undefined") return "/";
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
};

export const getReturnPathname = (path: string) => path.split(/[?#]/)[0] || "/";

const isPrintPath = (path: string) => getReturnPathname(path).startsWith("/print/");

export const isUnsafeRedirectPath = (path: string) =>
  path.startsWith("//") || /^[a-z][a-z\d+.-]*:/i.test(path);

const setSessionReturnPath = (path: string) => {
  try {
    sessionStorage.setItem(AUTH_RETURN_PATH_KEY, path);
  } catch {
    // Cookie/localStorage fallbacks still cover private-mode storage failures.
  }
};

const getSessionReturnPath = () => {
  try {
    return sessionStorage.getItem(AUTH_RETURN_PATH_KEY);
  } catch {
    return null;
  }
};

const removeSessionReturnPath = () => {
  try {
    sessionStorage.removeItem(AUTH_RETURN_PATH_KEY);
  } catch {
    // Ignore storage cleanup failures.
  }
};

const setReturnPathCookie = (path: string) => {
  if (typeof document === "undefined") return;
  // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API is not universal enough for auth fallback.
  document.cookie = `${AUTH_RETURN_PATH_COOKIE}=${encodeURIComponent(path)}; Path=/; Max-Age=${RETURN_PATH_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
};

const getReturnPathCookie = () => {
  if (typeof document === "undefined") return null;
  const cookie = document.cookie
    .split("; ")
    .find((item) => item.startsWith(`${AUTH_RETURN_PATH_COOKIE}=`));
  if (!cookie) return null;

  try {
    return decodeURIComponent(cookie.slice(AUTH_RETURN_PATH_COOKIE.length + 1));
  } catch {
    return null;
  }
};

const clearReturnPathCookie = () => {
  if (typeof document === "undefined") return;
  // biome-ignore lint/suspicious/noDocumentCookie: Must clear the same first-party fallback cookie.
  document.cookie = `${AUTH_RETURN_PATH_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`;
};

export const canUseAsAuthReturnPath = (path?: string | null) => {
  if (!path) {
    return false;
  }

  const pathname = getReturnPathname(path);

  return (
    path.startsWith("/") &&
    !NON_RETURN_PATHS.includes(pathname) &&
    !isPrintPath(path) &&
    !isUnsafeRedirectPath(path)
  );
};

export const saveAuthReturnPath = (path = getCurrentReturnPath()): boolean => {
  if (!canUseAsAuthReturnPath(path)) {
    return false;
  }

  setSessionReturnPath(path);
  storage.safeSet(AUTH_RETURN_PATH_KEY, path);
  setReturnPathCookie(path);
  return true;
};

export const readAuthReturnPath = () => {
  return (
    [
      getSessionReturnPath(),
      storage.safeGet(AUTH_RETURN_PATH_KEY),
      storage.safeGet(REDIRECT_PATH_KEY),
      getReturnPathCookie(),
    ].find(canUseAsAuthReturnPath) ?? null
  );
};

export const createAuthReturnState = (path = readAuthReturnPath() ?? getCurrentReturnPath()) => {
  if (!canUseAsAuthReturnPath(path)) {
    return null;
  }

  return `${AUTH_RETURN_STATE_PREFIX}${path}`;
};

export const readAuthReturnPathFromState = (state?: string | null) => {
  if (!state?.startsWith(AUTH_RETURN_STATE_PREFIX)) {
    return null;
  }

  const returnPath = state.slice(AUTH_RETURN_STATE_PREFIX.length);
  return canUseAsAuthReturnPath(returnPath) ? returnPath : null;
};

export const readAdditionalInfoReturnPath = (
  search = typeof window === "undefined" ? "" : window.location.search,
) => {
  const params = new URLSearchParams(search);
  return [params.get(ADDITIONAL_INFO_RETURN_PARAM)].find(canUseAsAuthReturnPath) ?? null;
};

export const buildAdditionalInfoRedirectPath = (
  returnPath: string | null = readAuthReturnPath(),
) => {
  const safeReturnPath = canUseAsAuthReturnPath(returnPath) ? returnPath : null;

  if (!safeReturnPath) {
    return "/additional-info";
  }

  const params = new URLSearchParams();
  params.set(ADDITIONAL_INFO_RETURN_PARAM, safeReturnPath);
  return `/additional-info?${params.toString()}`;
};

export const clearAuthReturnPath = () => {
  removeSessionReturnPath();
  storage.safeRemove(AUTH_RETURN_PATH_KEY);
  storage.safeRemove(REDIRECT_PATH_KEY);
  clearReturnPathCookie();
};

export const requireAdditionalInfoOnNextLogin = (
  path = getCurrentReturnPath(),
  options: { force?: boolean; saveReturnPath?: boolean } = {},
): boolean => {
  if (isPrintPath(path)) {
    return false;
  }

  const shouldSaveReturnPath = options.saveReturnPath ?? true;
  const hasReturnPath = shouldSaveReturnPath ? saveAuthReturnPath(path) : false;

  if (!hasReturnPath && !options.force) {
    return false;
  }

  sessionStorage.setItem(REQUIRE_ADDITIONAL_INFO_KEY, "true");
  return true;
};

export const clearAdditionalInfoRequirement = () => {
  sessionStorage.removeItem(REQUIRE_ADDITIONAL_INFO_KEY);
};
